import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';

interface Pest {
  id: string;
  name: string;
  scientificName: string;
  category: string;
  dangerLevel: 'Low' | 'Medium' | 'High';
  description: string;
  image: string;
  commonSigns: string[];
  habitat: string;
  seasonality: string;
}

@Component({
  selector: 'app-pest-library',
  templateUrl: './pest-library.component.html',
  styleUrl: './pest-library.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MegamenuComponent,
    FooterComponent
  ]
})
export class PestLibraryComponent {
  selectedCategory = 'All';
  
  categories = ['All', 'Insects', 'Arachnids', 'Rodents'];

  pests: Pest[] = [
    {
      id: 'ants',
      name: 'Ants',
      scientificName: 'Formicidae',
      category: 'Insects',
      dangerLevel: 'Low',
      description: 'Social insects that live in colonies and can invade homes searching for food and water.',
      image: '/assets/Ant-Control-Sugar.jpeg',
      commonSigns: ['Ant trails', 'Small piles of dirt', 'Food contamination'],
      habitat: 'Outdoors in soil, indoors in kitchens and pantries',
      seasonality: 'Spring through Fall'
    },
    {
      id: 'spiders',
      name: 'Spiders',
      scientificName: 'Araneae',
      category: 'Arachnids',
      dangerLevel: 'Medium',
      description: 'Eight-legged arachnids that help control other pests but can be unwelcome in homes.',
      image: '/assets/Spider-Control-Alive-Web.jpeg',
      commonSigns: ['Webs in corners', 'Egg sacs', 'Live spiders'],
      habitat: 'Dark corners, basements, garages, outdoor vegetation',
      seasonality: 'Year-round, more active in warmer months'
    },
    {
      id: 'cockroaches',
      name: 'Cockroaches',
      scientificName: 'Blattodea',
      category: 'Insects',
      dangerLevel: 'High',
      description: 'Resilient insects that can spread diseases and contaminate food sources.',
      image: '/assets/RoachOnBackInHome.jpeg',
      commonSigns: ['Droppings', 'Musty odor', 'Egg cases', 'Grease marks'],
      habitat: 'Kitchens, bathrooms, basements, warm and humid areas',
      seasonality: 'Year-round'
    },
    {
      id: 'wasps',
      name: 'Wasps',
      scientificName: 'Vespidae',
      category: 'Insects',
      dangerLevel: 'High',
      description: 'Aggressive stinging insects that can pose serious threats, especially to allergic individuals.',
      image: '/assets/Wasp-Control-Alive-Group.jpeg',
      commonSigns: ['Visible nests', 'Increased wasp activity', 'Buzzing sounds'],
      habitat: 'Eaves, attics, trees, ground burrows',
      seasonality: 'Spring through Fall'
    },
    {
      id: 'rodents',
      name: 'Rodents',
      scientificName: 'Rodentia',
      category: 'Rodents',
      dangerLevel: 'High',
      description: 'Mice and rats that can damage property and spread diseases through droppings and urine.',
      image: '/assets/RatWithTechInBG.jpeg',
      commonSigns: ['Droppings', 'Gnaw marks', 'Scratching sounds', 'Nests'],
      habitat: 'Attics, basements, walls, kitchens, garages',
      seasonality: 'Year-round, more active in cooler months'
    },
    {
      id: 'mosquitoes',
      name: 'Mosquitoes',
      scientificName: 'Culicidae',
      category: 'Insects',
      dangerLevel: 'Medium',
      description: 'Blood-sucking insects that can transmit diseases and make outdoor activities unpleasant.',
      image: '/assets/Mosquito-Control-Alive.jpeg',
      commonSigns: ['Buzzing sounds', 'Bites on skin', 'Standing water'],
      habitat: 'Areas with standing water, tall grass, shaded areas',
      seasonality: 'Spring through Fall'
    },
    {
      id: 'black-widow',
      name: 'Black Widow Spider',
      scientificName: 'Latrodectus hesperus',
      category: 'Arachnids',
      dangerLevel: 'High',
      description: 'Venomous spider with distinctive red hourglass marking, native to Utah.',
      image: '/assets/Black-Widow-Control-Closeup.jpeg',
      commonSigns: ['Irregular webs', 'Red hourglass marking', 'Egg sacs'],
      habitat: 'Dark, undisturbed areas like sheds, garages, woodpiles',
      seasonality: 'Most active in warmer months'
    },
    {
      id: 'crickets',
      name: 'Crickets',
      scientificName: 'Gryllidae',
      category: 'Insects',
      dangerLevel: 'Low',
      description: 'Chirping insects that can become nuisances when they invade homes in large numbers.',
      image: '/assets/Cricket-Control-Dead.jpeg',
      commonSigns: ['Chirping sounds', 'Damage to fabrics', 'Live insects'],
      habitat: 'Basements, crawl spaces, outdoor vegetation',
      seasonality: 'Late summer through fall'
    }
  ];

  constructor(private router: Router) {}

  get filteredPests() {
    if (this.selectedCategory === 'All') {
      return this.pests;
    }
    return this.pests.filter(pest => pest.category === this.selectedCategory);
  }

  selectCategory(category: string) {
    this.selectedCategory = category;
  }

  viewPestDetails(pestId: string) {
    this.router.navigate(['/pest-library', pestId]);
  }

  getDangerColor(level: string): string {
    switch (level) {
      case 'High': return '#dc3545';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  }
}